import React, { useState } from 'react'; // Importing React and the useState hook
import { Box, Typography, IconButton, Paper, Menu, MenuItem, Avatar, Tooltip } from '@mui/material'; // Importing MUI components for layout and buttons
import MoreVertIcon from '@mui/icons-material/MoreVert'; // Importing the menu icon
import Chip from '@mui/material/Chip'; // Importing Chip component for displaying labels
import TimerRoundedIcon from '@mui/icons-material/TimerRounded'; // Importing the timer icon
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'; // Importing the assignment icon
import CardDetailsDialog from './CardDetailsDialog';
import RiskCardDetailsDialog from './RiskCardDetailsDialog';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';//imports for the type: Activity 
import BugReportIcon from '@mui/icons-material/BugReport';//imports for the type: Defect 
import AutoStoriesIcon from '@mui/icons-material/AutoStories';//imports for the type: Story 
import TaskAltIcon from '@mui/icons-material/TaskAlt';//imports for the type: Task 


// Utility to check if a card matches a rule's conditions
function cardMatchesRule(card, rule) {
  return rule.conditions.every(cond => {
    let cardField = cond.field;
    if (cardField === 'status') cardField = 'cardStatus'; // Map 'status' to 'cardStatus'
    if (cardField === 'assignee') cardField = 'assigneeName'; // Map 'assignee' to 'assigneeName'
    if (cardField === 'assigneeEmail') cardField = 'assigneeEmail'; // Map 'assigneeEmail' to 'assigneeEmail'
    const cardValue = card[cardField];
    
    switch (cond.operator) {
      case 'equals':
        if (cardField === 'assigneeName' || cardField === 'assigneeEmail') {
          return cardValue?.toLowerCase() === cond.value?.toLowerCase();
        }
        return cardValue === cond.value;
      case 'not_equals':
        if (cardField === 'assigneeName' || cardField === 'assigneeEmail') {
          return cardValue?.toLowerCase() !== cond.value?.toLowerCase();
        }
        return cardValue !== cond.value;
      case 'greater_than':
        if (cardField === 'dueDate') {
          const cardDate = new Date(cardValue);
          const compareDate = new Date(cond.value);
          return cardDate > compareDate;
        }
        return cardValue > cond.value;
      case 'less_than':
        if (cardField === 'dueDate') {
          const cardDate = new Date(cardValue);
          const compareDate = new Date(cond.value);
          return cardDate < compareDate;
        }
        return cardValue < cond.value;
      case 'within_days':
        if (cardField === 'dueDate' && cardValue) {
          const now = new Date();
          const dueDate = new Date(cardValue);
          const diffTime = dueDate - now;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          const days = parseInt(cond.value);
          return diffDays >= 0 && diffDays <= days;
        }
        return false;
      default:
        return false;
    }
  });
}
//isDragOver = false added to Card component to handle drag over state
const Card = ({ card, handleDragStart, handleOpenEditDialog, handleDeleteCard, handleDuplicateCard, rules = [], isDragOver = false, columns = [], isSprintContext }) => {
 
  const [anchorEl, setAnchorEl] = useState(null); // State to manage the anchor element for the menu
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false); // State to manage the details dialog visibility

  // Function to handle menu button click
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget); // Set the anchor element to the clicked button
  };

  // Function to close the menu
  const handleMenuClose = () => {
    setAnchorEl(null); // Reset the anchor element
  };

  // Function to handle editing the card Changes made here
  // This function will be called when the user clicks the edit option in the menu
  // const handleEdit = () => {
  //   handleOpenEditDialog(card); // Call the provided handler with the current card
  //   handleMenuClose(); // Close the menu
  // };

  // // Function to handle deleting the card Changes here
  // const handleDelete = () => {
  //   if (isSprintContext) {
  //     // Remove from sprint only
  //     if (window.confirm(`Remove ${card.taskName} from this sprint?`)) {
  //       handleDeleteCard(card.id); // Call the delete handler with the card ID
  //     } else {
  //       if (window.confirm(`Are you sure you want to delete ${card.taskName}?`)) {
  //         handleDeleteCard(card.id); // Call the delete handler with the card ID
  //       }
  //     }
  //   }

  //   handleMenuClose(); // Close the menu
  // }; 

  // Simplified this function. It just calls the function passed from CardPage.
  const handleDeleteOrRemove = () => {
    handleDeleteCard(card.id); 
    handleMenuClose();
  };
  // Function to handle click on the task name, opening the details dialog
  const handleTaskNameClick = () => {
    setOpenDetailsDialog(true); // Set the state to open the details dialog
  };

  // Function to close the details dialog
  const handleDetailsDialogClose = () => {
    setOpenDetailsDialog(false); // Set the state to close the dialog
  };

// Function to handle edit request from inside CardDetailsDialog
const handleEditCard = (cardData) => {
  setOpenDetailsDialog(false);      // Close the details view
  handleOpenEditDialog(cardData);   // Notify parent to open the edit dialog
};

const handleEdit = () => {
    handleOpenEditDialog(card);
    handleMenuClose();
  };

const handleDuplicate = () => {
    handleDuplicateCard(card);
    handleMenuClose();
  };

const getCardTypeIcon = (type) => {
  const normalizedType = type?.toLowerCase();

  switch (normalizedType) {
    case 'activity':
      return (
        <Tooltip title="Activity" arrow>
          <DirectionsRunIcon sx={{ fontSize: '1rem', color: '#1976d2' }} />
        </Tooltip>
      );
    case 'defect':
      return (
        <Tooltip title="Defect" arrow>
          <BugReportIcon sx={{ fontSize: '1rem', color: '#f44336' }} />
        </Tooltip>
      );
    case 'story':
      return (
        <Tooltip title="Story" arrow>
          <AutoStoriesIcon sx={{ fontSize: '1rem', color: '#9c27b0' }} />
        </Tooltip>
      );
    case 'task':
      return (
        <Tooltip title="Task" arrow>
          <TaskAltIcon sx={{ fontSize: '1rem', color: '#4caf50' }} />
        </Tooltip>
      );
    default:
      return null;
  }
};



  // Determine if the card's status is "Done"
  const isDone = card.cardStatus === 'Done';

  // Find highlight color from rules
  const highlightColor = (() => {
    for (const rule of rules) {
      if (rule.isActive) {
        for (const block of rule.blocks || []) {
          if (block.actions && block.conditions && cardMatchesRule(card, { conditions: block.conditions })) {
            // Support new set_card_highlight action
            const highlightAction = block.actions.find(action => action.value === 'set_card_highlight');
            if (highlightAction && highlightAction.color) {
              return highlightAction.color;
            }
            // Support legacy set_card_red action
            if (block.actions.some(action => action.value === 'set_card_red')) {
              return 'red';
            }
          }
        }
      }
    }
    return null;
  })();

  // Check if card matches due date notification rule
  const hasDueDateNotification = rules.some(
    rule =>
      rule.isActive &&
      (rule.blocks || []).some(block =>
        block.actions && block.actions.some(action => action.value === 'send_email') &&
        block.conditions && cardMatchesRule(card, { conditions: block.conditions })
      )
  );

  // Add warning icon if card has due date notification
  const renderDueDateWarning = () => {
    if (hasDueDateNotification) {
      return (
        <Tooltip title="Email notification active">
          <TimerRoundedIcon 
            sx={{ 
              color: '#f44336',
              fontSize: '1.2rem',
              marginRight: '0.5rem'
            }} 
          />
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <>
      <Paper
        data-card-id={card.id}
        sx={{
          mb: 2, // Margin bottom
          p: 0, // No padding
          borderRadius: 2, // Rounded corners
          backgroundColor: '#E8EAE6', // Background color of the card
          position: 'relative', // Positioning context for child elements
          cursor: isDone ? 'default' : 'move', // Cursor style based on status
          display: 'flex', // Use flexbox layout
          flexDirection: 'column', // Arrange children in a column
          transition: 'box-shadow 0.3s, transform 0.3s', // Transition for hover effects
          border: highlightColor ? `3px solid ${highlightColor}` : (isDragOver ? '2px dashed #1976d2' : 'none'), // Add red border if rule matches or dashed border when dragging over
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)', // Box shadow on hover
            transform: isDone ? 'none' : 'scale(1.02)', // Scale effect on hover
          },
        }}
        draggable={!isDone} // Make card draggable unless it's done
        onDragStart={!isDone ? (event) => handleDragStart(event, card) : null} // Handle drag start
      >
        {/* Top Section */}
        <Box
          sx={{
            backgroundColor: '#1976d2', // Background color for the header
            p: 1, // Padding
            display: 'flex', // Flexbox layout
            alignItems: 'center', // Center align items vertically
            // justifyContent: 'space-between', // Space items between
            height: '40px', // Fixed height for header
            width: "100%"
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', width: "40%"}}> {/* Clickable assignee name */}
              {/* card.taskName */} {/* card.assigneeName */} {/* Display assignee name */}
              {/* <Box> Flex container for assignee */}
                {/* <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}> */}
          

                <Typography
                variant="caption" // Caption variant for ID
                color="black" // ID text color
                sx={{
                  fontSize: '0.7rem', // Font size
                  display: 'block', // Block display
                  marginTop: '-2px', // Slight upward margin
                  lineHeight: '1', // Line height
                  color: 'white', // Text color
                  marginTop: '0.2rem'
                }}
                >
                  {card.cardId} {/* Display card ID */}
                </Typography>
              {/* </Box> */}
          </Box>

          <Box display="flex" flexDirection="row" width="60%" justifyContent={'flex-end'} alignItems={'center'}>
            {renderDueDateWarning()}
            <Chip
            label={card.priority} // Display card priority
            size="small" // Small size
            sx={{
            backgroundColor:
              card.priority === 'High'
                ? '#f44336' // High priority color
                : card.priority === 'Medium'
                ? '#ff9800' // Medium priority color
                : card.priority === 'Low'
                ? '#4caf50' // Low priority color
                : card.priority === 'Urgent'
                ? '#ab333f' // Urgent priority color
                : '#d3d3d3', // Default color
            color: '#fff', // Text color
            fontSize: '0.75rem', // Font size
            borderRadius: '25px', // Rounded corners
            padding: '1px 1px', // Padding
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)', // Shadow effect
            }}
            />

            <IconButton
              onClick={handleMenuClick} // Handle menu button click
              aria-label="more options" // Accessibility label
              sx={{
              color: 'text.secondary', // Icon color
              padding: '0.5rem', // Padding
              size: '1px', // Size
              }}
              disabled={isDone} // Disable button if card is done
            > 
              <MoreVertIcon sx={{ fontSize: '1.2rem' }} />  {/* Menu icon */}
            </IconButton>
            {/* Menu for edit and delete actions */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleEdit} disabled={isDone}>Edit</MenuItem>
              {!isSprintContext && (
                <MenuItem onClick={handleDuplicate} disabled={isDone}>Duplicate</MenuItem>
              )}
              {/* {isSprintContext ? (
                <MenuItem onClick={() => { handleDelete(); }} disabled={isDone}>Remove</MenuItem>
              ) : (
              <MenuItem onClick={handleDelete} disabled={isDone}>Delete</MenuItem>
              )} */}
              <MenuItem 
                onClick={handleDeleteOrRemove} 
                disabled={isDone}
                sx={{ color: isSprintContext ? 'inherit' : 'error.main' }}
              >
                {isSprintContext ? 'Remove' : 'Delete'}
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Middle Section */}
        <Box         
          sx={{
            backgroundColor: '#87CEEB', // Background color for middle section
            flexGrow: 1, // Allow to grow
            p: 1, // Padding
          }}
        >
          <Box onClick={handleTaskNameClick} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}> {/* Flex container for assignee */}
            {/* <AssignmentIndIcon sx={{ mr: 1 }} /> Assignee icon */}
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
              {/* card.assigneeName */} {card.taskName} {/* Display task name */}
            </Typography>
          </Box>

        </Box>

        {/* Bottom Section */}
        <Box
          sx={{
            backgroundColor: '#d3d3d3', // Background color for bottom section
            p: 1, // Padding
            display: 'flex', // Flexbox layout
            alignItems: 'center', // Center align items
            justifyContent: 'space-between', // Space items between
            height: '20px', // Fixed height
          }}
        >
          {card.dueDate && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}> {/* Flex container for due date */}
              <TimerRoundedIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.8rem' }} /> {/* Timer icon */}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {new Date(card.dueDate).toLocaleDateString('en-US', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })} {/* Display formatted due date */}
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap={1}>
  {/* Card Type Icon */}
  {getCardTypeIcon(card.cardType)}

  {/* Assignee Avatar */}
  <Tooltip title={card.assigneeName || "Unknown"} arrow>
    <Avatar sx={{ bgcolor: 'green', width: 17, height: 17, fontSize: '0.70rem' }}>
      {card.assigneeName ? card.assigneeName.charAt(0).toUpperCase() : "?"}
    </Avatar>
  </Tooltip>
</Box>

        </Box>
      </Paper>

      {/* Dialog for displaying card details */}
      {card.cardType === 'Risk' ? (
        <RiskCardDetailsDialog open={openDetailsDialog} onClose={handleDetailsDialogClose} card={card} />
      ) : (
        <CardDetailsDialog open={openDetailsDialog} onClose={handleDetailsDialogClose} card={card} onEdit={handleEditCard} columns={columns} />
      )}
    </>
  );
};

export { cardMatchesRule }; // Export the utility function
export default Card; // Export the Card component as default

